import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import LockIcon from '@mui/icons-material/Lock';
import { Tooltip, Typography } from '@mui/material';
import { useConfirmation } from 'components/Confirmation';
import store from 'store';

import { IReworkingMeetsModel } from '@common/models/Reworkings/ReworkingMeets';
import { IReworkingSlotModel } from '@common/models/Reworkings/ReworkingSlot';

import MeetModal from './MeetModal';
import AppointmentsBookingModal from './Modal';
import { StyledAppointmentsBookingItemWrap } from './styled';

const MAX_HOURS_BEFORE = 12;

function getStatus(status?: number) {
	const STATUSES: Record<number, any> = {
		0: {
			label: 'Free',
			bg: 'info.main',
			color: 'primary.main',
		},
		1: {
			label: 'Busy',
			bg: 'primary.main',
			color: 'primary.contrastText',
		},
	};
	return (
		STATUSES[status] || {
			label: 'Booked by you',
			bg: 'warning.main',
			color: 'warning.contrastText',
		}
	);
}

const MEET_STATUSES: Record<number, any> = {
	1: {
		label: 'Completed',
		bg: 'success.main',
		color: 'success.contrastText',
	},
	2: {
		label: 'Canceled',
		bg: 'warning.main',
		color: 'warning.contrastText',
	},
};

interface PropsType {
	slot: IReworkingSlotModel;
	reworkingMeets: IReworkingMeetsModel;
	teacher: any;
	filters: any;
}

function AppointmentsBookingItem({ slot, reworkingMeets, teacher, filters }: PropsType) {
	const { t } = useTranslation();

	const canBook = slot.startDateTime.diffNow('hours').hours > MAX_HOURS_BEFORE && slot.status === 0;
	const canView = slot.status === 1 && slot.student_id === store.profile.student?.id;
	const confirm = useConfirmation();
	const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
	const meet = reworkingMeets.data.find((meetToFind) => meetToFind.meetSlot?.id === slot.id);
	const slotStatus =
		slot.student_id === store.profile.student?.id && meet?.status
			? MEET_STATUSES[meet.status]
			: getStatus(slot.student_id !== store.profile.student?.id ? slot.status : null);
	const lesson = meet?.gradebook?.schedule_lesson?.lesson;

	const formattedDate = slot.endDateTime.toFormat('EEE, dd.MM');

	const handleSubmit = async (values: any) => {
		await reworkingMeets.addMeet({
			meet_slot_id: slot.id,
			student_id: store.profile.student?.id,
			teacher_id: teacher.id,
			...values,
		});
		slot.setStatus(1, store.profile.student?.id);
		setIsBookingModalOpen(false);
		await store.modalNotifications.fetch({ service_id: store.services.current.id, is_modal: 1 });
	};

	return (
		<>
			<StyledAppointmentsBookingItemWrap
				{...((canBook || canView) && {
					onClick: () => setIsBookingModalOpen(true),
				})}
				sx={{ backgroundColor: slotStatus.bg }}
			>
				<Typography variant="subtitle3" sx={{ color: slotStatus.color }}>
					{slot.timesString} {slotStatus.label}
				</Typography>
				{!canBook && slot.status === 0 && (
					<Tooltip title={`Less than ${MAX_HOURS_BEFORE} hours before start`}>
						<LockIcon sx={{ color: slotStatus.color, fontSize: '1em' }} />
					</Tooltip>
				)}
			</StyledAppointmentsBookingItemWrap>
			{slot.student_id === store.profile.student?.id ? (
				<MeetModal
					open={isBookingModalOpen}
					onClose={() => setIsBookingModalOpen(false)}
					onSubmit={() => {
						confirm({
							text: t('appointment.youWantToCancel'),
							title: t('appointment.wait'),
						})
							.then(async () => {
								await meet.remove();
								slot.setStatus(0, null);
							})
							.finally(() => {
								setIsBookingModalOpen(false);
							});
					}}
					initialValues={{
						program: meet?.programGroup?.program?.id,
						lesson: lesson?.id,
						type: meet?.type,
						time: slot.timesString,
						day: formattedDate,
						phone: meet?.phone,
						zoom_access_code: meet?.zoom_access_code,
						zoom_id: meet?.zoom_id,
						zoom_link: meet?.zoom_link,
						miro_link: meet?.miro_link,
						comment_teacher: meet?.comment_teacher,
					}}
					lesson={lesson}
				/>
			) : (
				<AppointmentsBookingModal
					filters={filters}
					open={isBookingModalOpen}
					onClose={() => setIsBookingModalOpen(false)}
					onSubmit={handleSubmit}
					initialValues={{
						program: '',
						lesson: '',
						type: 'working_off',
						time: slot.timesString,
						day: formattedDate,
						phone: store.profile.phone,
						zoom_access_code: teacher.zoom_access_code,
						zoom_id: teacher.zoom_id,
						zoom_link: teacher.zoom_link,
					}}
				/>
			)}
		</>
	);
}

export default observer(AppointmentsBookingItem);
